/**
 * The alert center, mounted once in the app shell: it loads the inbox summary,
 * listens to the alert stream and owns the cards on screen and the "while you
 * were away" popup.
 *
 * - On every visit the popup lists every alert not yet shown (none is skipped;
 *   owner, 2026-10-25), grouped. What was shown is marked seen on the server,
 *   per user, so another tab or the next visit doesn't show it again.
 * - New alerts arrive over `/live/alerts/stream` (Server-Sent Events, read with
 *   fetch since EventSource can't send the sign-in header) and appear as
 *   cards; at most 3 at once, the rest wait in the bell (and in the popup on
 *   the next visit until shown). If the stream drops, it reconnects asking for
 *   everything after the newest alert it has, and the summary is re-read every
 *   minute meanwhile.
 * - Closing or reading a card in one tab closes it in every tab (the stream's
 *   `state` events).
 */
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import { api } from "@/api/client";
import { API_BASE } from "@/api/config";
import { getAccessToken, hasRefreshToken, refreshAccess } from "@/api/session";

import {
  CenterContext,
  FALLBACK_MS,
  MAX_CARDS,
  useInboxSummary,
  type Center,
} from "./center";
import { useAlertsEnabled } from "./hooks";
import {
  channelOn,
  inQuietHours,
  INBOX_KEY,
  SUMMARY_KEY,
  type AlertItem,
  type InboxSummary,
} from "./inbox";

// ------------------------------------------------------------------ the stream

type StreamEvent =
  | { event: "hello"; data: { last_id: number; unread: number } }
  | { event: "alerts"; data: { items: AlertItem[]; unread: number } }
  | {
      event: "state";
      data: {
        changes: {
          id: number;
          read: boolean;
          dismissed: boolean;
          seen: boolean;
          snoozed_until: string | null;
        }[];
        unread: number;
      };
    }
  | { event: "end" | "error"; data: unknown };

function parse(block: string): StreamEvent | null {
  let event = "";
  const data: string[] = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data.push(line.slice(5).trim());
  }
  if (!event || !data.length) return null;
  try {
    return { event, data: JSON.parse(data.join("\n")) } as StreamEvent;
  } catch {
    return null;
  }
}

/** Read the stream until it ends; resolves when it does (the caller reconnects). */
async function readStream(
  after: number,
  onEvent: (e: StreamEvent) => void,
  signal: AbortSignal,
): Promise<"end" | "retry" | "stop"> {
  if (!getAccessToken() && hasRefreshToken()) await refreshAccess();
  const token = getAccessToken();
  if (!token) return "stop";
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/live/alerts/stream?after=${after}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "text/event-stream",
      },
      signal,
    });
  } catch {
    return signal.aborted ? "stop" : "retry";
  }
  if (res.status === 401 && hasRefreshToken()) {
    await refreshAccess();
    return "retry";
  }
  if (!res.ok || !res.body)
    return res.status === 402 || res.status === 403 ? "stop" : "retry";
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return "end";
      buffer += decoder.decode(value, { stream: true });
      let cut = buffer.indexOf("\n\n");
      while (cut >= 0) {
        const ev = parse(buffer.slice(0, cut));
        buffer = buffer.slice(cut + 2);
        if (ev) onEvent(ev);
        cut = buffer.indexOf("\n\n");
      }
    }
  } catch {
    return signal.aborted ? "stop" : "retry";
  }
}

// ------------------------------------------------------------------ desktop + sound

function desktopNotify(item: AlertItem): boolean {
  if (
    typeof Notification === "undefined" ||
    Notification.permission !== "granted"
  )
    return false;
  try {
    // the tag stops the same alert from several tabs showing twice
    const n = new Notification(item.title, {
      body: item.body ?? undefined,
      tag: `rai-alert-${item.id}`,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
    return true;
  } catch {
    return false;
  }
}

let audio: AudioContext | null = null;
/** A soft two-note chime (price alerts and strategy watches). */
function chime() {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return;
    audio ??= new Ctor();
    const t = audio.currentTime;
    for (const [i, f] of [880, 1320].entries()) {
      const o = audio.createOscillator();
      const g = audio.createGain();
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t + i * 0.12);
      g.gain.exponentialRampToValueAtTime(0.08, t + i * 0.12 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.12 + 0.35);
      o.connect(g).connect(audio.destination);
      o.start(t + i * 0.12);
      o.stop(t + i * 0.12 + 0.4);
    }
  } catch {
    // no sound available: the card is enough
  }
}

// ------------------------------------------------------------------ the provider

const PHONE = "(max-width: 639px)";
function subscribeWidth(cb: () => void) {
  if (typeof window === "undefined" || !window.matchMedia)
    return () => undefined;
  const m = window.matchMedia(PHONE);
  m.addEventListener("change", cb);
  return () => m.removeEventListener("change", cb);
}
/** 3 cards on a desktop, 1 on a phone (the rest wait in the bell, not shown yet). */
function useMaxCards(): number {
  return useSyncExternalStore(
    subscribeWidth,
    () =>
      typeof window !== "undefined" && window.matchMedia?.(PHONE).matches
        ? 1
        : MAX_CARDS,
    () => MAX_CARDS,
  );
}

export function AlertCenterProvider({
  children,
  enabled: shellEnabled,
}: {
  children: ReactNode;
  enabled: boolean;
}) {
  const enabled = useAlertsEnabled() && shellEnabled;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const summary = useInboxSummary(shellEnabled);
  const [cards, setCards] = useState<AlertItem[]>([]);
  const [popup, setPopup] = useState<AlertItem[] | null>(null);
  const [bellOpen, setBellOpen] = useState(false);
  const [first, setFirst] = useState<InboxSummary | null>(null);
  const lastId = useRef(0);
  const posted = useRef(new Set<number>());
  const settingsRef = useRef(summary.data?.settings);
  useEffect(() => {
    settingsRef.current = summary.data?.settings;
  }, [summary.data]);

  // First summary of the visit: the popup with everything unseen, and the cards still open.
  if (summary.data && !first) {
    setFirst(summary.data);
    setCards(summary.data.cards);
    if (summary.data.unseen.length) setPopup(summary.data.unseen);
  }

  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: SUMMARY_KEY });
    void qc.invalidateQueries({ queryKey: INBOX_KEY });
  }, [qc]);

  const setUnread = useCallback(
    (unread: number) => {
      qc.setQueryData<InboxSummary>(SUMMARY_KEY, (old) =>
        old ? { ...old, unread } : old,
      );
    },
    [qc],
  );

  // New alerts from the stream: cards, desktop notifications and the chime.
  const arrive = useCallback((items: AlertItem[]) => {
    const settings = settingsRef.current;
    const quiet = inQuietHours(settings?.prefs);
    const now = Date.now();
    const fresh = items.filter(
      (i) =>
        i.card &&
        channelOn(settings, i.setting ?? i.kind, "card") &&
        !(i.show_until && Date.parse(i.show_until) < now),
    );
    if (fresh.length)
      setCards((cur) => [
        ...fresh.filter((f) => !cur.some((c) => c.id === f.id)).reverse(),
        ...cur,
      ]);
    const hidden =
      typeof document !== "undefined" && document.visibilityState !== "visible";
    const shown: number[] = [];
    for (const i of items) {
      if (
        !quiet &&
        hidden &&
        channelOn(settings, i.setting ?? i.kind, "desktop") &&
        desktopNotify(i)
      )
        shown.push(i.id);
    }
    if (shown.length)
      void api
        .post("/me/inbox/desktop", { body: { ids: shown } })
        .catch(() => undefined);
    if (
      !quiet &&
      settings?.prefs.sound !== false &&
      items.some((i) => i.sound && channelOn(settings, i.setting ?? i.kind, "card"))
    )
      chime();
    for (const i of items) lastId.current = Math.max(lastId.current, i.id);
  }, []);

  // The stream, reconnecting with growing waits; a dropped stream also re-reads the summary.
  useEffect(() => {
    if (!enabled || !first) return;
    lastId.current = Math.max(lastId.current, first.last_id);
    const ctrl = new AbortController();
    let wait = 3_000;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const run = async () => {
      if (ctrl.signal.aborted) return;
      const out = await readStream(
        lastId.current,
        (e) => {
          wait = 3_000;
          if (e.event === "alerts") {
            arrive(e.data.items);
            setUnread(e.data.unread);
            void qc.invalidateQueries({ queryKey: INBOX_KEY });
          } else if (e.event === "state") {
            const gone = new Set(
              e.data.changes
                .filter(
                  (c) =>
                    c.read ||
                    c.dismissed ||
                    (c.snoozed_until &&
                      Date.parse(c.snoozed_until) > Date.now()),
                )
                .map((c) => c.id),
            );
            if (gone.size)
              setCards((cur) => cur.filter((c) => !gone.has(c.id)));
            setUnread(e.data.unread);
            void qc.invalidateQueries({ queryKey: INBOX_KEY });
          }
        },
        ctrl.signal,
      );
      if (out === "stop" || ctrl.signal.aborted) return;
      if (out === "retry") {
        refresh();
        wait = Math.min(wait * 2, FALLBACK_MS);
      }
      timer = setTimeout(() => void run(), out === "end" ? 500 : wait);
    };
    void run();
    return () => {
      ctrl.abort();
      if (timer) clearTimeout(timer);
    };
    // the stream starts once the first summary is in; it keeps its own place after that
  }, [enabled, first, arrive, setUnread, refresh, qc]);

  // Cards on screen are "seen": they won't pop up again on return (any tab).
  const max = useMaxCards();
  const visible = cards.slice(0, max);
  const key = visible
    .filter((c) => !c.seen)
    .map((c) => c.id)
    .join(",");
  useEffect(() => {
    const ids = key
      .split(",")
      .filter(Boolean)
      .map(Number)
      .filter((id) => !posted.current.has(id));
    if (!ids.length) return;
    for (const id of ids) posted.current.add(id);
    void api.post("/me/inbox/seen", { body: { ids } }).catch(() => undefined);
  }, [key]);

  // The popup's alerts are seen as soon as it shows.
  const popupKey = popup?.map((p) => p.id).join(",") ?? "";
  useEffect(() => {
    if (!popup?.length) return;
    void api
      .post("/me/inbox/seen", { body: { ids: popup.map((p) => p.id) } })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupKey]);

  const center = useMemo<Center>(() => {
    const drop = (ids: number[]) =>
      setCards((cur) => cur.filter((c) => !ids.includes(c.id)));
    return {
      enabled,
      summary: summary.data,
      cards: visible,
      hidden: Math.max(0, cards.length - max),
      popup,
      bellOpen,
      setBellOpen,
      closePopup: () => setPopup(null),
      dismiss: (item) => {
        drop([item.id]);
        void api
          .post("/me/inbox/dismiss", { body: { ids: [item.id] } })
          .finally(refresh);
      },
      read: (ids) => {
        drop(ids);
        void api.post("/me/inbox/read", { body: { ids } }).finally(refresh);
      },
      readAll: (upTo, group) => {
        const up = upTo ?? summary.data?.last_id ?? lastId.current;
        setCards([]);
        void api
          .post("/me/inbox/read", {
            body: { up_to: up, ...(group ? { group: group as never } : {}) },
          })
          .finally(refresh);
      },
      open: (item) => {
        drop([item.id]);
        setBellOpen(false);
        setPopup(null);
        void api
          .post("/me/inbox/{inbox_id}/click", { path: { inbox_id: item.id } })
          .finally(refresh);
        if (item.link) void navigate({ href: item.link });
      },
      snooze: (item, minutes = 60) => {
        drop([item.id]);
        void api
          .post("/me/inbox/{inbox_id}/snooze", {
            path: { inbox_id: item.id },
            body: { minutes },
          })
          .then(() => setTimeout(refreshCards, minutes * 60_000))
          .catch(() => undefined);
      },
    };
    function refreshCards() {
      void qc
        .fetchQuery({
          queryKey: SUMMARY_KEY,
          queryFn: () => api.get("/me/inbox/summary"),
        })
        .then((d) => {
          setCards((cur) => [
            ...d.cards.filter((c) => !cur.some((x) => x.id === c.id)),
            ...cur,
          ]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, summary.data, cards, popup, bellOpen, refresh, navigate, qc]);

  return (
    <CenterContext.Provider value={center}>{children}</CenterContext.Provider>
  );
}
